import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { BankSlip } from '../../types';
import { localeDate, tz, money } from '../../utils/region';

// ── Payment Slip cell (Bank Statement table) ─────────────────────────────────
// One upload zone per transaction (owner spec 2026-08-18): click the button, drag files onto the
// cell (the cell highlights while a drag hovers it, so the drop can't land on the wrong row), or
// click the cell and paste from the clipboard. Images + PDFs only, ≤10 MB each, one or many.
// The cell is a VERTICAL STACK (owner 2026-10-18, report edd70bcd) — this REVERSES the 2026-09-28
// "one slip in the column, however many were uploaded" rule and the '+N' badge that came with it:
// an empty row shows the dashed SLIP box alone, and from the first slip onwards every slip on the
// row is listed one under another so the pile reads as one document. The thumbnails carry NO
// caption (report edd70bcd, 2nd opening: "Remove document name") — the pile is the pictures
// themselves, each keeping its own × or its CLIENT / TBC chip, and the column was narrowed to suit.
// "Add another" sits under the last slip INSIDE this column — never a second box beside it. Each
// thumbnail renders inline (PDFs via pdf.js first page, same treatment as DD reports). Clicking any
// of them opens a lightbox holding every slip on the row, one page per slip in upload order, and
// each page confirms or removes its own file.
// HEIC/HEIF are refused at the door: Chrome cannot render them, and a slip nobody can open is
// worse than a clear "convert to JPG" moment at upload time.
//
// SlipThumb and SlipLightbox are exported since 2026-10-11 (owner: lease-registration slips): the
// Bank Statement's pending-slip bar and the Lease Registration tab draw the same thumbnail and the
// same lightbox, fetching the bytes through their own route via `loader`. Without a loader both
// behave exactly as this cell always has (api.bankSlipObjectUrl).

const MAX_MB = 10;
export type SlipLoader = (scope: string, slipId: string) => Promise<string>;
const defaultLoader: SlipLoader = (scope, id) => api.bankSlipObjectUrl(scope, id);
// A lease-registration slip's chip reads LEASE REG whatever its state; a quotation slip keeps TBC → CLIENT.
const chipText = (slip: BankSlip, confirmed: boolean) => (slip.lease ? 'LEASE REG' : confirmed ? 'CLIENT' : 'TBC');
const CHUNK = 15;               // files per request — stays under the server's 20-file multer cap
const isPdf = (m: string, name = '') => /pdf/i.test(m || '') || /\.pdf$/i.test(name);
const accepted = (f: File) =>
  /^image\/(svg\+xml|hei[cf])$/.test(f.type || '') ? false :
  (f.type || '').startsWith('image/') || f.type === 'application/pdf' ||
  /\.(pdf|png|jpe?g|webp|gif|bmp)$/i.test(f.name || '');

// pdf.js on demand — the worker ships as a static file in public/ (already there for DD/FileLibrary).
let pdfLibP: Promise<any> | null = null;
function getPdfLib(): Promise<any> {
  if (!pdfLibP) {
    pdfLibP = import('pdfjs-dist/legacy/build/pdf').then((m: any) => {
      const lib = m.default || m;
      lib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      return lib;
    });
  }
  return pdfLibP;
}
async function pdfPageDataUrl(data: ArrayBuffer, pageNo: number, targetW: number): Promise<{ url: string; pages: number }> {
  const pdfjs = await getPdfLib();
  // ⛔ isEvalSupported: false (review 2026-11-03): utils/pdfRender.ts says why. An uploaded PDF is whatever somebody uploaded, and this reader runs on the app's own origin.
  const doc = await pdfjs.getDocument({ data: data.slice(0), isEvalSupported: false }).promise;
  const page = await doc.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(4, Math.max(0.2, (targetW * 2) / base.width));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  await page.render({ canvasContext: ctx, viewport }).promise;
  const url = canvas.toDataURL('image/jpeg', 0.9);
  const pages = doc.numPages;
  try { doc.destroy(); } catch { /* best effort */ }
  return { url, pages };
}

// One slip's thumbnail — the stack draws one of these per slip on the row. LAZY: nothing is fetched
// until the thumb scrolls into view — a slip-heavy 100-row page must not download every original up
// front. Images keep their object URL while mounted; PDF blobs are revoked the moment the bytes are
// copied out (only the tiny JPEG thumb survives).
export function SlipThumb({ scope, slip, onOpen, onDelete, loader = defaultLoader }: {
  scope: string; slip: BankSlip; onOpen: () => void; onDelete: () => void; loader?: SlipLoader;
}) {
  const { t } = useTranslation();
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const hostRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) { setVisible(true); io.disconnect(); } }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    let live = true; let kept = '';
    setSrc(''); setFailed(false);
    (async () => {
      let url = '';
      try {
        url = await loader(scope, slip.id);
        if (!live) { URL.revokeObjectURL(url); return; }
        if (!isPdf(slip.mime, slip.filename)) { kept = url; setSrc(url); return; }
        const buf = await (await fetch(url)).arrayBuffer();
        URL.revokeObjectURL(url); url = '';
        if (!live) return;
        const { url: thumb } = await pdfPageDataUrl(buf, 1, 44);
        if (live) setSrc(thumb);
      } catch { if (url) URL.revokeObjectURL(url); if (live) setFailed(true); }
    })();
    return () => { live = false; if (kept) setTimeout(() => URL.revokeObjectURL(kept), 1500); };
  }, [scope, slip.id, visible]); // eslint-disable-line
  return (
    <span ref={hostRef} className="bs-slip-thumb" title={slip.filename} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <span onClick={onOpen} style={{ width: 44, height: 44, borderRadius: 6, border: '1px solid var(--border)', background: '#fff', overflow: 'hidden', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in' }}>
        {failed ? <i className="ti ti-file-alert" style={{ fontSize: 18, color: 'var(--text-tertiary)' }} />
          : src ? <img src={src} alt={slip.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <i className="ti ti-loader" style={{ fontSize: 15, color: 'var(--text-tertiary)', display: 'inline-block', animation: 'spin 1s linear infinite' }} />}
      </span>
      {isPdf(slip.mime, slip.filename) && (
        <span style={{ position: 'absolute', left: 2, bottom: 2, fontSize: 8, fontWeight: 800, padding: '0 3px', borderRadius: 3, background: '#B42318', color: '#fff', pointerEvents: 'none' }}>PDF</span>
      )}
      {/* Client-uploaded slip (from the public quotation page): read-only, badged, no delete.
          Amber TBC until an approver confirms it really is the slip for THIS transaction
          (owner 2026-09-18) — a client can upload anything, so it is a claim until checked. */}
      {!!slip.client && (
        <span title={slip.lease
            ? t('bs_lease_tip', { ref: slip.ref_label || '' }) + (Number(slip.confirmed_at) ? ` · ${t('bs_slip_confirmed_by', { who: slip.confirmed_by_name || '' })}` : ` · ${t('bs_slip_tbc_note')}`)
            : Number(slip.confirmed_at)
            ? t('bs_slip_client_ok_tip', { who: slip.confirmed_by_name || '', file: slip.filename })
            : t('bs_slip_client_tbc_tip', { file: slip.filename, when: slip.created_at ? new Date(slip.created_at * 1000).toLocaleDateString(localeDate(), { timeZone: tz() }) : '' })}
          style={{ position: 'absolute', left: 2, top: 2, fontSize: 7.5, fontWeight: 800, padding: '0 3px', borderRadius: 3, color: '#fff', background: Number(slip.confirmed_at) ? '#1D7C4D' : '#B54708' }}>
          {chipText(slip, !!Number(slip.confirmed_at))}
        </span>
      )}
      {!slip.client && <button onClick={e => { e.stopPropagation(); onDelete(); }} title={t('bs_slip_delete')}
        style={{ position: 'absolute', right: -5, top: -5, width: 16, height: 16, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--bg-surface)', color: '#B42318', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}>
        <i className="ti ti-x" style={{ fontSize: 10 }} />
      </button>}
    </span>
  );
}

// One page of the lightbox = one slip: the image full-size, or every page of a PDF stacked inside
// this same page. A file that cannot be decoded (corrupt / password-protected PDF) says so instead
// of a silent black gap. The bar on top carries everything about THIS slip — which one of how many,
// its name, its client/TBC status, Confirm and Remove. It is STICKY (owner 2026-09-19: "you have to
// scroll all the way to the bottom to confirm"): a phone screenshot at 880px wide is taller than the
// 92vh box, so the buttons ride along at the top while their page is on screen. Remove sits beside
// Confirm: any bank-module user may remove a slip, while confirming stays Calem/Nick, so the two
// buttons deliberately have different audiences.
function SlipPage({ scope, slip, n, total, txnId, canConfirm, onConfirmed, onRemove, loader = defaultLoader }: {
  scope: string; slip: BankSlip; n: number; total: number; txnId?: string; canConfirm?: boolean;
  onConfirmed?: (slip: BankSlip) => void; onRemove?: () => void; loader?: SlipLoader;
}) {
  const { t } = useTranslation();
  const [imgs, setImgs] = useState<string[]>([]);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirmed = !!Number(slip.confirmed_at);

  // Confirm = "this IS the slip for this transaction". Only Calem and Nick; the button is merely
  // hidden from everyone else — the server 403s them (owner 2026-09-18). The lightbox stays open:
  // the page turns "Confirmed by …" in place, and the row's other slips are still there to check.
  const toggleConfirm = async () => {
    if (saving || !txnId) return;
    if (confirmed && !window.confirm(t('bs_slip_unconfirm_ask'))) return;
    setSaving(true);
    try {
      let r: any;
      try { r = await api.bankSlipConfirm(scope, slip.id, txnId, !confirmed); }
      catch (e: any) {
        // A lease-registration slip re-pinned to a credit that is not the amount the website expects:
        // the server answers 409 amount_differs and wants the reason it records (owner 2026-10-11).
        if (e?.status !== 409 || e?.payload?.code !== 'amount_differs') throw e;
        const reason = (window.prompt(t('bs_lease_amount_differs', { expected: money(e.payload.expected), actual: money(e.payload.actual) }), '') || '').trim();
        if (!reason) return;
        r = await api.bankSlipConfirm(scope, slip.id, txnId, true, reason);
      }
      if (r.slip) onConfirmed?.(r.slip);
    } catch (e: any) { alert(e.message); } finally { setSaving(false); }
  };
  useEffect(() => {
    let live = true; let kept = '';
    (async () => {
      let url = '';
      try {
        url = await loader(scope, slip.id);
        if (!live) { URL.revokeObjectURL(url); return; }
        if (!isPdf(slip.mime, slip.filename)) { kept = url; setImgs([url]); setBusy(false); return; }
        const buf = await (await fetch(url)).arrayBuffer();
        URL.revokeObjectURL(url); url = '';
        const first = await pdfPageDataUrl(buf, 1, 900);
        if (!live) return;
        const pages: string[] = [first.url];
        setImgs([...pages]); setBusy(false);
        for (let p = 2; p <= first.pages; p++) {
          const { url: u } = await pdfPageDataUrl(buf, p, 900);
          if (!live) return;
          pages.push(u); setImgs([...pages]);
        }
      } catch { if (url) URL.revokeObjectURL(url); if (live) { setFailed(true); setBusy(false); } }
    })();
    return () => { live = false; if (kept) setTimeout(() => URL.revokeObjectURL(kept), 1500); };
  }, [scope, slip.id]); // eslint-disable-line
  return (
    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 1, alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: '8px 12px', flexWrap: 'wrap', background: 'rgba(24,24,27,0.92)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, padding: '9px 14px' }}>
        <span style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 800, color: '#18181B', background: '#fff', borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' }}>
          {t('bs_slip_page', { n, total })}
        </span>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ color: '#fff', fontSize: 12.5, fontWeight: 700, wordBreak: 'break-all' }}>
            {/* Same CLIENT / TBC badge the column puts on a client's slip; LEASE REG for a website slip,
                followed by which registration it belongs to. */}
            {!!slip.client && (
              <span style={{ marginRight: 7, fontSize: 9.5, fontWeight: 800, padding: '1px 5px', borderRadius: 4, color: '#fff', background: confirmed ? '#1D7C4D' : '#B54708', verticalAlign: '1px' }}>
                {chipText(slip, confirmed)}
              </span>
            )}
            {!!slip.lease && !!slip.ref_label && <span style={{ marginRight: 7, color: '#FDE68A' }}>{slip.ref_label} ·</span>}
            {slip.filename}
          </div>
          {!!slip.client && (
            <div style={{ marginTop: 3, fontSize: 12, fontWeight: 700, color: confirmed ? '#A7F3D0' : '#FDE68A' }}>
              <i className={`ti ti-${confirmed ? 'circle-check' : 'alert-triangle'}`} style={{ fontSize: 13, marginRight: 5, verticalAlign: '-2px' }} />
              {confirmed
                ? t('bs_slip_confirmed_by', { who: slip.confirmed_by_name || '—' })
                : t('bs_slip_tbc_note')}
            </div>
          )}
        </div>
        {!!slip.client && canConfirm && txnId && (
          <button onClick={toggleConfirm} disabled={saving}
            style={{ border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12.5, fontWeight: 800, cursor: saving ? 'default' : 'pointer', color: '#fff', background: confirmed ? '#B42318' : '#1D7C4D', opacity: saving ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {saving && <i className="ti ti-loader" style={{ fontSize: 13, display: 'inline-block', animation: 'spin 1s linear infinite' }} />}
            {confirmed ? t('bs_slip_unconfirm') : t('bs_slip_confirm')}
          </button>
        )}
        {onRemove && (
          <button onClick={onRemove} disabled={saving} title={t('bs_slip_remove_tip')}
            style={{ borderRadius: 7, padding: '7px 16px', fontSize: 12.5, fontWeight: 800, cursor: saving ? 'default' : 'pointer', color: '#FECDCA', background: 'transparent', border: '1px solid #B42318', opacity: saving ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <i className="ti ti-trash" style={{ fontSize: 14 }} />{t('bs_slip_remove')}
          </button>
        )}
      </div>
      {busy && <i className="ti ti-loader" style={{ fontSize: 28, color: '#fff', display: 'inline-block', animation: 'spin 1s linear infinite' }} />}
      {failed && (
        <div style={{ color: '#FECDCA', fontSize: 13, fontWeight: 600, background: 'rgba(180,35,24,0.35)', border: '1px solid #B42318', borderRadius: 10, padding: '12px 18px', maxWidth: 420, textAlign: 'center' }}>
          <i className="ti ti-file-alert" style={{ fontSize: 20, display: 'block', marginBottom: 6 }} />{t('bs_slip_preview_fail')}
        </div>
      )}
      {imgs.map((u, i) => (
        <img key={i} src={u} alt={`${slip.filename} ${i + 1}`} style={{ maxWidth: '100%', borderRadius: 8, background: '#fff', boxShadow: '0 8px 40px #0007' }} />
      ))}
    </div>
  );
}

// Lightbox: EVERY slip on the row, one page per slip in upload order (Calem 2026-09-28: "one page
// showing the first payment slip, a second page showing the second payment slip…"). It draws the
// row's slips as they stand, so a removed slip's page drops straight out. Esc / × / backdrop closes.
export function SlipLightbox({ scope, slips, txnId, canConfirm, onConfirmed, onRemove, onClose, loader = defaultLoader }: {
  scope: string; slips: BankSlip[]; txnId?: string; canConfirm?: boolean;
  onConfirmed?: (slip: BankSlip) => void; onRemove?: (slip: BankSlip) => void; onClose: () => void; loader?: SlipLoader;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 12000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      {/* The × centred in its circle by a flex box, as the other round closes are (Bugify #836d4443, 2nd opening) */}
      <button onClick={onClose} title={t('close')}
        style={{ position: 'fixed', top: 14, right: 16, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 17, cursor: 'pointer', zIndex: 12001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="ti ti-x" />
      </button>
      {/* Fixed width, so every page's bar lines up whatever size each file turns out to be. */}
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(94vw, 880px)', maxHeight: '92vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {slips.map((s, i) => (
          <SlipPage key={s.id} scope={scope} slip={s} n={i + 1} total={slips.length} txnId={txnId} canConfirm={canConfirm}
            onConfirmed={onConfirmed} onRemove={onRemove && (() => onRemove(s))} loader={loader} />
        ))}
      </div>
    </div>
  );
}

export default function SlipCell({ scope, txnId, slips, canConfirm, onChanged }: {
  scope: string; txnId: string; slips: BankSlip[]; canConfirm?: boolean; onChanged: (slips: BankSlip[]) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const dragDepth = useRef(0);

  // The lightbox pages through whatever the row holds right now; once its last slip is gone there
  // is nothing left to show, so it closes (and a later upload does not pop it back open).
  useEffect(() => { if (!slips.length) setOpen(false); }, [slips.length]);

  const uploadFiles = async (files: File[]) => {
    if (!files.length) return;
    if (busy) { alert(t('bs_slip_busy')); return; }
    const ok: File[] = [];
    const bad: string[] = [], big: string[] = [];
    for (const f of files) {
      if (!accepted(f)) { bad.push(f.name || '?'); continue; }
      if (f.size > MAX_MB * 1024 * 1024) { big.push(f.name || '?'); continue; }
      ok.push(f);
    }
    if (bad.length) alert(t('bs_slip_invalid') + '\n' + bad.join('\n'));
    if (big.length) alert(t('bs_slip_too_big', { mb: MAX_MB }) + '\n' + big.join('\n'));
    if (!ok.length) return;
    setBusy(true);
    try {
      // Chunked so a big drop never trips the server's per-request file cap.
      const failed: string[] = [];
      for (let i = 0; i < ok.length; i += CHUNK) {
        const r: any = await api.bankSlipUpload(scope, txnId, ok.slice(i, i + CHUNK));
        onChanged(r.slips || []);
        if (r.rejected?.length) failed.push(...r.rejected);
        if (r.failed?.length) failed.push(...r.failed);
      }
      if (failed.length) alert(t('bs_slip_invalid') + '\n' + failed.join('\n'));
    } catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };

  const del = async (s: BankSlip) => {
    if (busy) return;
    // A client's slip is their own evidence of payment, so the prompt says whose it is.
    if (!window.confirm(s.client ? t('bs_slip_remove_client_confirm', { file: s.filename }) : t('bs_slip_delete_confirm'))) return;
    setBusy(true);
    try {
      const r: any = await api.bankSlipDelete(scope, s.id);
      // r.slips covers the STAFF slips only. Client slips live in another table and are merged in
      // by the list endpoint, so they have to be carried across by hand — rebuilding the cell from
      // r.slips alone would make every remaining client slip vanish until the next reload.
      const keptClient = slips.filter(x => !!x.client && x.id !== s.id);
      onChanged(r.slips ? [...r.slips, ...keptClient] : slips.filter(x => x.id !== s.id));
    }
    catch (e: any) { alert(e.message); } finally { setBusy(false); }
  };

  // Drag & drop with a per-cell highlight — enter/leave are counted because children fire them
  // too, and only real FILE drags light up (dragging a thumbnail or text must not promise a drop).
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onDragEnter = (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.stopPropagation(); dragDepth.current++; setDrag(true); };
  const onDragOver = (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.stopPropagation(); };
  const onDragLeave = (e: React.DragEvent) => { if (!hasFiles(e)) return; e.preventDefault(); e.stopPropagation(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDrag(false); };
  const onDrop = (e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault(); e.stopPropagation();
    dragDepth.current = 0; setDrag(false);
    uploadFiles(Array.from(e.dataTransfer?.files || []));
  };
  const onPaste = (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length) { e.preventDefault(); e.stopPropagation(); uploadFiles(files); }
  };

  // The dashed SLIP box and the "add another" line are the same <label> round the same input — only
  // one of them is ever on screen, so the picker is written once here.
  const picker = (
    <input type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif,image/bmp,application/pdf,.pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp" style={{ display: 'none' }} disabled={busy}
      onChange={e => { uploadFiles(Array.from(e.target.files || [])); e.currentTarget.value = ''; }} />
  );

  return (
    <div
      tabIndex={0}
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      onPaste={onPaste}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      title={t('bs_slip_hint')}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start',
        minHeight: 50, padding: 4, borderRadius: 8, outline: 'none', cursor: 'default',
        border: drag ? '2px dashed var(--accent)' : focus ? '2px solid var(--accent)' : '2px dashed transparent',
        background: drag ? 'var(--bg-active)' : 'transparent',
        transition: 'border-color .12s, background .12s',
      }}>
      {/* The pile: the thumbnails alone, in upload order, each with its own × (a client's slip
          carries its CLIENT / TBC chip instead). No caption beside them (report edd70bcd, 2nd
          opening) — hovering a thumbnail still names its file and the lightbox names every slip,
          so the words were only costing the column width. The 6px gap between them is the
          clearance the × needs: it hangs 5px above its own thumbnail. */}
      {slips.map(s => (
        <SlipThumb key={s.id} scope={scope} slip={s} onOpen={() => setOpen(true)} onDelete={() => del(s)} />
      ))}
      {slips.length === 0 ? (
        <label onClick={e => e.stopPropagation()} title={t('bs_slip_add')}
          style={{ width: 44, height: 44, borderRadius: 6, border: '1.5px dashed var(--border-strong)', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: busy ? 'default' : 'pointer', color: 'var(--text-tertiary)', flexShrink: 0, gap: 1 }}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-camera-plus'} style={{ fontSize: 16, ...(busy ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : {}) }} />
          {!busy && <span style={{ fontSize: 7.5, fontWeight: 700, letterSpacing: '.02em' }}>SLIP</span>}
          {picker}
        </label>
      ) : (
        /* Add another — a thin line under the last slip, so another one joins THIS pile instead of
           standing in a box of its own beside it. It keeps reading on ONE line (report edd70bcd, 2nd
           opening): nowrap, and the icon, the gap and the side padding are trimmed, so it adds little
           to the column, which fits its content. No maxWidth, so the dashed border always closes
           round the words rather than cutting through them. */
        <label onClick={e => e.stopPropagation()} title={t('bs_slip_add')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 3px', borderRadius: 6, border: '1.5px dashed var(--border-strong)', cursor: busy ? 'default' : 'pointer', color: 'var(--text-tertiary)', fontSize: 8.5, fontWeight: 700, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-camera-plus'} style={{ fontSize: 10, ...(busy ? { display: 'inline-block', animation: 'spin 1s linear infinite' } : {}) }} />
          {t('bs_slip_add_more')}
          {picker}
        </label>
      )}
      {/* Paste affordance: only while the cell is focused, so the table stays clean. Width 0 and nowrap: the words run
          out past their box, as they did past the old fixed column, so the table doesn't count them and a click on the
          cell can't widen the column (report ca1243c8). */}
      {focus && !busy && <span style={{ width: 0, whiteSpace: 'nowrap', fontSize: 9.5, color: 'var(--accent)', fontWeight: 700 }}>{t('bs_slip_paste_hint')}</span>}
      {open && !!slips.length && <SlipLightbox scope={scope} slips={slips} txnId={txnId} canConfirm={canConfirm}
        onConfirmed={one => onChanged(slips.map(x => (x.id === one.id ? { ...x, ...one } : x)))}
        onRemove={del}
        onClose={() => setOpen(false)} />}
    </div>
  );
}
